import React from 'react';
import { Cake } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

const CakesSection = ({
  pasteles,
  openLightbox,
  openCakeConfigurator,
  openTresLechesConfigurator,
  addSimpleCakeToCart,
  getImageUrl
}) => {
  return (
    <section id="cakes" className="py-20 px-6 bg-gradient-to-b from-cream to-white">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <Cake className="w-16 h-16 mx-auto mb-4 text-caramel" />
          <h2 className="text-4xl md:text-5xl font-bold text-chocolate mb-4">Nuestros Pasteles & Roscas</h2>
          <p className="text-xl text-chocolate-light max-w-2xl mx-auto">
            Pasteles y roscas artesanales para cada ocasión especial
          </p>
        </div>

        {/* La Reina - Configurable Cake */}
        {pasteles.filter(cake => cake.isConfigurable && !cake.isTresLeches && cake.slug === 'la-reina').map(cake => (
          <div key={cake.id} className="mb-16">
            <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-r from-amber-50 to-cream">
              <div className="grid md:grid-cols-2 gap-0">
                <div 
                  className="aspect-square overflow-hidden cursor-pointer relative group"
                  onClick={() => openLightbox(cake.image, cake.name)}
                >
                  <img 
                    src={getImageUrl(cake.image)} 
                    alt={cake.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-caramel text-white px-4 py-2 rounded-full font-bold shadow-lg">
                    ⭐ La Favorita
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                      🔍 Ver imagen
                    </span>
                  </div>
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-chocolate mb-2">{cake.name}</h3>
                  <p className="text-chocolate-light mb-6">{cake.description}</p>
                  
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    {cake.sizes.map(size => (
                      <div key={size.id} className="text-center bg-white rounded-xl p-4 shadow-md">
                        <div className="relative w-24 h-24 mx-auto mb-3">
                          <svg viewBox="0 0 100 100" className="w-full h-full">
                            <circle cx="50" cy="50" r="45" fill="#FDF2E9" stroke="#D4A574" strokeWidth="2"/>
                            {Array.from({ length: size.id === 'mediana' ? 12 : 20 }).map((_, i) => {
                              const angle = (i * 360 / (size.id === 'mediana' ? 12 : 20)) * Math.PI / 180;
                              const x2 = 50 + 40 * Math.cos(angle);
                              const y2 = 50 + 40 * Math.sin(angle);
                              return (
                                <line 
                                  key={i} 
                                  x1="50" y1="50" 
                                  x2={x2} y2={y2} 
                                  stroke="#D4A574" 
                                  strokeWidth="1"
                                  opacity="0.5"
                                />
                              );
                            })}
                            <circle cx="50" cy="50" r="10" fill="#D4A574"/>
                          </svg>
                        </div>
                        <div className="font-bold text-chocolate">{size.name}</div>
                        <div className="text-sm text-chocolate-light">{size.portions}</div>
                        <div className="text-2xl font-bold text-caramel mt-2">${size.price}</div>
                      </div>
                    ))}
                  </div>

                  <Button 
                    onClick={() => openCakeConfigurator(cake)}
                    className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white text-lg py-6 transition-colors duration-200"
                  >
                    🎂 Personalizar Mi Pastel
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>
        ))}

        {/* Otros Cakes */}
        <h3 className="text-2xl font-bold text-chocolate text-center mb-8">Más Opciones</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Tres Leches */}
          {pasteles.filter(cake => cake.isTresLeches && cake.slug === 'tres-leches').map(cake => (
            <Card key={cake.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div 
                className="aspect-square overflow-hidden cursor-pointer relative"
                onClick={() => openLightbox(cake.image, cake.name)}
              >
                <img 
                  src={cake.image} 
                  alt={cake.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg">
                  🍰 4 Sabores
                </div>
                <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg">
                  🎁 PROMO
                </div>
                <div className="absolute bottom-2 left-2 bg-orange-700 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg">
                  📋 Bajo Pedido
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                    🔍 Ver imagen
                  </span>
                </div>
              </div>
              <CardContent className="p-4 space-y-3">
                <h4 className="text-lg font-bold text-chocolate text-center">{cake.name}</h4>
                <p className="text-xs text-chocolate-light text-center line-clamp-2">{cake.description}</p>
                
                <div className="flex items-center justify-center gap-3 py-2">
                  <div className="w-12 h-12">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="45" fill="#FDF2E9" stroke="#D4A574" strokeWidth="2"/>
                      {Array.from({ length: 8 }).map((_, i) => {
                        const angle = (i * 360 / 8) * Math.PI / 180;
                        const x2 = 50 + 35 * Math.cos(angle);
                        const y2 = 50 + 35 * Math.sin(angle);
                        return (
                          <line key={i} x1="50" y1="50" x2={x2} y2={y2} stroke="#D4A574" strokeWidth="1" opacity="0.5"/>
                        );
                      })}
                      <circle cx="50" cy="50" r="6" fill="#D4A574"/>
                    </svg>
                  </div>
                  <div className="text-xs text-chocolate-light">
                    <div className="font-semibold text-chocolate">desde ~6 personas</div>
                  </div>
                </div>
                
                <div className="text-center">
                  <span className="text-xs text-chocolate-light">desde </span>
                  <span className="text-2xl font-bold text-caramel">${cake.sizes[0].price}.00</span>
                </div>
                <Button 
                  onClick={() => openTresLechesConfigurator(cake)}
                  className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200"
                >
                  Ver Opciones
                </Button>
              </CardContent>
            </Card>
          ))}
          
          {/* Other non-configurable cakes */}
          {pasteles.filter(cake => !cake.isConfigurable).map(cake => (
            <Card key={cake.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div
                className="aspect-square overflow-hidden cursor-pointer relative"
                onClick={() => openLightbox(cake.image, cake.name)}
              >
                <img
                  src={cake.image}
                  alt={cake.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {cake.bajoPedido && (
                  <div className="absolute top-2 left-2 bg-orange-700 text-white px-2 py-1 rounded-full font-bold text-xs shadow-lg">
                    📋 Bajo Pedido
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                    🔍 Ver imagen
                  </span>
                </div>
              </div>
              <CardContent className="p-4 space-y-3">
                <h4 className="text-lg font-bold text-chocolate text-center">{cake.name}</h4>
                <p className="text-xs text-chocolate-light text-center line-clamp-2">{cake.description}</p>
                
                <div className="flex items-center justify-center gap-3 py-2">
                  <div className="w-12 h-12">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="45" fill="#FDF2E9" stroke="#D4A574" strokeWidth="2"/>
                      {Array.from({ length: 16 }).map((_, i) => {
                        const angle = (i * 360 / 16) * Math.PI / 180;
                        const x2 = 50 + 35 * Math.cos(angle);
                        const y2 = 50 + 35 * Math.sin(angle);
                        return (
                          <line key={i} x1="50" y1="50" x2={x2} y2={y2} stroke="#D4A574" strokeWidth="1" opacity="0.5"/>
                        );
                      })}
                      <circle cx="50" cy="50" r="6" fill="#D4A574"/>
                    </svg>
                  </div>
                  <div className="text-xs text-chocolate-light">
                    <div className="font-semibold text-chocolate">{cake.portions}</div>
                  </div>
                </div>
                
                <div className="text-center">
                  {cake.price > 0 ? (
                    <span className="text-2xl font-bold text-caramel">${cake.price?.toFixed(2)}</span>
                  ) : (
                    <span className="text-lg font-semibold text-caramel">Precio variable</span>
                  )}
                </div>
                <Button 
                  onClick={() => addSimpleCakeToCart(cake)}
                  className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200"
                >
                  Agregar al Pedido
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CakesSection;
